import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivationService } from 'libs/core/activation/activation.service';
import { ButtonComponent } from '@net3-multiplatform-client/libs';
import { finalize } from 'rxjs';


@Component({
  selector: 'activation-form',
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './activation-form.html'
})
export class ActivationForm {
  /* TODO
  validació de token que no estigui expirat contra backend?
  input-password per veure o amagar la contrasenya?
  control d'errors del backend per quan no funcioni l'activació
  */

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private activationService = inject(ActivationService);

  token = signal('');
  loading = signal(false);
  activated = signal(false);
  successMessage = signal('');

  form = this.fb.group(
    {
      password: ['', [this.entraPasswordSimpleValidator()]],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: [this.passwordsMatchValidator] }
  );

  tokenMissing = computed(() => !this.token());
  canSubmit = computed(() => !this.loading() && !this.tokenMissing());

  constructor() {
    const t = this.route.snapshot.queryParamMap.get('token')!;
    this.token.set(t);
  }

  private passwordsMatchValidator(group: any) {
    const p = group.get('password')?.value;
    const c = group.get('confirmPassword')?.value;
    return p && c && p !== c ? { passwordsMismatch: true } : null;
  }

  get password() {
    return this.form.get('password');
  }
  get confirmPassword() {
    return this.form.get('confirmPassword');
  }

  async onSubmit() {
    const token = this.token();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    const password = this.password?.value as string;

    this.activationService.activate(token, password)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.successMessage.set('Tu cuenta se ha activado correctamente. Pulsa "Continuar" para iniciar sesión.');
          this.activated.set(true);
        },
        error: (err) => {
          //TODO control errors del backend
        }
      });
  }

  goToSignIn() {
    this.router.navigate(['/signin']);
  }

  private entraPasswordSimpleValidator(): ValidatorFn {
    const asciiPrintable = /^[\x20-\x7E]+$/;          // ASCII imprimible (inclou espai)
    const upper = /[A-Z]/;
    const lower = /[a-z]/;
    const digit = /[0-9]/;
    const symbol = /[^A-Za-z0-9\s]/;                 // símbol = no lletra, no número, no espai

    return (control: AbstractControl): ValidationErrors | null => {
      const v = (control.value ?? '') as string;
      if (!v) return { entraPwd: 'required' };
      if (v.length < 8) return { entraPwd: 'min' };
      if (v.length > 256) return { entraPwd: 'max' };
      if (!asciiPrintable.test(v)) return { entraPwd: 'ascii' };

      const categories = [upper.test(v), lower.test(v), digit.test(v), symbol.test(v)].filter(Boolean).length;
      if (categories < 3) return { entraPwd: 'complexity' };

      return null;
    };
  }

  get passwordErrorText(): string | null {
    if (!this.password || !this.password.touched) return null;

    const code = this.password.errors?.['entraPwd'];
    if (!code) return null;

    switch (code) {
      case 'required':
        return 'Introduce una contraseña.';
      case 'min':
        return 'Mínimo 8 carácteres.';
      case 'max':
        return 'Máximo 256 carácteres.';
      case 'complexity':
        return 'Ha de contener como mínimo 3 tipos: mayúscula, minúscula, número o símbolo. Ej: Comunidad!2025.';
      default:
        return 'Contraseña no válida.';
    }
  }

}
